import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, UserPlus, HandHeart, Globe, Users } from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import { IMAGES } from "@/data/content";
import api from "@/lib/api";

const OPTIONS = [
  { key: "member", title: "Devenir Membre", icon: UserPlus, desc: "Rejoignez notre communauté de croyants engagés dans la mission." },
  { key: "volunteer", title: "Volontaire", icon: HandHeart, desc: "Offrez votre temps et vos talents pour soutenir nos projets." },
  { key: "mission", title: "Partir en Mission", icon: Globe, desc: "Rejoignez une équipe missionnaire pour atteindre les nations." },
  { key: "department", title: "Rejoindre un Département", icon: Users, desc: "Intégrez l'un de nos départements selon vos dons et appels." },
];

const DEPARTMENTS = [
  "Évangélisation",
  "Prière",
  "Formation",
  "Affaires Sociales",
  "Communication & Marketing",
];

const Join = () => {
  const [interest, setInterest] = useState("member");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "Haïti",
    department: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/join", { ...form, interest });
      toast.success("Inscription reçue ! Notre équipe vous contactera très bientôt.");
      setForm({ name: "", email: "", phone: "", country: "Haïti", department: "", message: "" });
    } catch {
      toast.error("Erreur. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="join-page">
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img src={IMAGES.evangelisation} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D91]/90 via-[#0B3D91]/60 to-[#0B3D91]/30" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mv-container py-24 text-white">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Rejoindre</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Rejoignez la mission.
          </h1>
          <p className="mt-6 text-lg text-blue-50/95 max-w-2xl">
            Devenez membre, volontaire ou partenaire de MIREVSA et participez à transformer des vies.
          </p>
        </div>
      </section>

      {/* HOW TO ENGAGE */}
      <section className="mv-section bg-white" data-testid="how-to-engage-section">
        <div className="mv-container">
          <div className="max-w-2xl mb-12">
            <SectionEyebrow>Comment s'engager</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C]">
              Quatre façons de rejoindre.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {OPTIONS.map((o) => {
              const Icon = o.icon;
              const active = interest === o.key;
              return (
                <button
                  key={o.key}
                  onClick={() => setInterest(o.key)}
                  data-testid={`join-option-${o.key}`}
                  className={`text-left p-8 transition-all ${
                    active ? "bg-[#0B3D91] text-white" : "bg-white hover:bg-[#F8FAFC]"
                  }`}
                >
                  <Icon size={28} className={active ? "text-white" : "text-[#0B3D91]"} />
                  <h3 className={`mt-5 font-heading font-bold text-lg ${active ? "text-white" : "text-[#1C1C1C]"}`}>
                    {o.title}
                  </h3>
                  <p className={`mt-2 text-sm leading-relaxed ${active ? "text-blue-100" : "text-muted"}`}>
                    {o.desc}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="mv-section bg-[#F8FAFC]">
        <div className="mv-container max-w-3xl">
          <div className="mb-10">
            <SectionEyebrow>Formulaire</SectionEyebrow>
            <h2 className="mt-4 font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1C1C1C]">
              Formulaire d'engagement.
            </h2>
            <p className="mt-4 text-base text-muted leading-relaxed">
              Remplissez ce formulaire et nous vous contacterons pour les prochaines étapes.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white p-8 md:p-12 border border-gray-200" data-testid="join-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="label-eyebrow">Nom complet <span className="text-red-500">*</span></label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="join-name"
                  className="mt-2 w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
              <div>
                <label className="label-eyebrow">Email <span className="text-red-500">*</span></label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="join-email"
                  className="mt-2 w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
              <div>
                <label className="label-eyebrow">Téléphone</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="join-phone"
                  className="mt-2 w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
              <div>
                <label className="label-eyebrow">Type d'engagement <span className="text-red-500">*</span></label>
                <select
                  required
                  value={interest}
                  onChange={(e) => setInterest(e.target.value)}
                  data-testid="join-interest"
                  className="mt-2 w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91] bg-white"
                >
                  {OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>{o.title}</option>
                  ))}
                </select>
              </div>
              {interest === "department" && (
                <div className="md:col-span-2">
                  <label className="label-eyebrow">Département souhaité</label>
                  <select
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    data-testid="join-department"
                    className="mt-2 w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91] bg-white"
                  >
                    <option value="">— Sélectionner —</option>
                    {DEPARTMENTS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="label-eyebrow">Message (optionnel)</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="join-message"
                  className="mt-2 w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[#0B3D91]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="join-submit"
              className="mt-8 btn-primary w-full md:w-auto disabled:opacity-60"
            >
              {loading ? "Envoi..." : "Envoyer ma demande"} <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Join;
